import React, { useEffect, useRef, useState } from "react";
import { CellAddress, CoObjectCellItem, CoObjectItem, CoObjectSummary } from "../../interfaces/CoObject";
import { Icon, ImageFit, Link, Stack } from "@fluentui/react";
import { IconButton } from "@fluentui/react/lib/Button";
import { CoObjectTypeEnum } from "../../../api/models";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";
import { formatMessageDefault } from "../../../utils/helpers/LanguageHelper";
import { CustomPill } from "../../generic/TrackedChangeOperationTypePill";
import { ObjectListItemRow } from "./ObjectListItemRow";
import { selectWordCell } from "../../../office/ContentControl";
import { renderCellTitle } from "../../../utils/helpers/TagsHelper";
import { WithLoader } from "../../../common/loading/WithLoader";

// Module-level singleton: only one row can be in listening mode at a time
let cancelActiveListener: (() => void) | null = null;

type InitialSelectedCell = CoObjectSummary | CoObjectItem;

export interface ObjectListItemButton {
  key: string;
  icon: string;
  onClick: (item: CoObjectItem) => void;
}

export const ObjectListItemRowExpandable = ({
  objectItem,
  onClick,
  onAddCells,
  onCellListChange,
  onSelectedCellsChange,
  onListeningChange,
  disabled = false,
  showObjectIcon = true,
  showStatusIcons = true,
  showComponentIcons = true,
  highlighted = false,
  buttons = [],
  initialSelectedCells = [],
}: {
  objectItem: CoObjectItem;
  onClick?: (item: CoObjectItem, shiftKey?: boolean) => void;
  onAddCells?: (item: CoObjectItem) => void;
  onCellListChange?: (items: CoObjectItem[]) => void;
  onSelectedCellsChange?: (tableItem: CoObjectItem, selectedCells: CoObjectItem[]) => void;
  onListeningChange?: (isListening: boolean) => void;
  disabled?: boolean;
  showObjectIcon?: boolean;
  showStatusIcons?: boolean;
  showComponentIcons?: boolean;
  highlighted?: boolean;
  buttons?: ObjectListItemButton[];
  initialSelectedCells?: InitialSelectedCell[];
}) => {
  const [expanded, setExpanded] = useState(false);
  const [cellList, setCellList] = useState<CoObjectCellItem[]>([]);
  const [selectedCellKeys, setSelectedCellKeys] = useState<Set<string>>(new Set());
  const [isListening, setIsListening] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);

  useEffect(() => {
    if (!initialSelectedCells.length) return;

    const rebuilt = initialSelectedCells
      .map<CoObjectCellItem | null>((cell) => {
        const address = "cellAddress" in cell ? (cell.cellAddress as CellAddress | undefined) : undefined;
        const row = address?.row ?? ("row" in cell ? cell.row : null);
        const column = address?.column ?? ("column" in cell ? cell.column : null);
        if (row == null || column == null) return null;

        const cellAddress: CellAddress = { row, column };
        const text = "text" in cell ? cell.text : undefined;
        const objectName =
          "info" in cell && cell.info.objectType === CoObjectTypeEnum.TABLE_CELL
            ? cell.info.objectName
            : renderCellTitle(cellAddress, text);

        return {
          ...objectItem,
          id: `${objectItem.id}_${row}-${column}`,
          cellAddress,
          info: {
            ...objectItem.info,
            objectType: CoObjectTypeEnum.TABLE_CELL,
            objectName,
          },
        } as CoObjectCellItem;
      })
      .filter((cell): cell is CoObjectCellItem => cell !== null);

    setCellList(rebuilt);
    setSelectedCellKeys(new Set(rebuilt.map((r) => cellKey(r.cellAddress))));
    setExpanded(true);
    onSelectedCellsChange?.(objectItem, rebuilt);
  }, []);

  const handlerActiveRef = useRef(false);
  const handlerFnRef = useRef<((arg?: unknown) => void | Promise<void>) | null>(null);
  const cellListRef = useRef<CoObjectCellItem[]>([]);
  const onListeningChangeRef = useRef(onListeningChange);

  useEffect(() => {
    onListeningChangeRef.current = onListeningChange;
  }, [onListeningChange]);

  useEffect(() => {
    cellListRef.current = cellList;
  }, [cellList]);

  useEffect(() => {
    return () => {
      if (handlerActiveRef.current && handlerFnRef.current) {
        Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
          handler: handlerFnRef.current,
        });
        cancelActiveListener = null;
      }
    };
  }, []);

  const cellKey = (addr: CellAddress) => `${addr.row}-${addr.column}`;

  const removeListener = () => {
    if (!handlerActiveRef.current || !handlerFnRef.current) return;
    Office.context.document.removeHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      { handler: handlerFnRef.current },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          handlerActiveRef.current = false;
          handlerFnRef.current = null;
          cancelActiveListener = null;
          setIsListening(false);
          onListeningChangeRef.current?.(false);
        } else {
          console.error("Errore rimozione handler:", result.error);
        }
      }
    );
  };

  const onSelectionChanged = async () => {
    setShowPlaceholder(true);
    setExpanded(true);

    try {
      await Word.run(async (context) => {
        const sel = context.document.getSelection();
        sel.load(["parentTableCellOrNullObject", "parentContentControlOrNullObject"]);
        await context.sync();

        const cell = sel.parentTableCellOrNullObject;
        if (cell.isNullObject) {
          removeListener();
          return;
        }

        const cc = sel.parentContentControlOrNullObject;
        cell.load(["rowIndex", "cellIndex", "body/text"]);
        if (!cc.isNullObject) {
          cc.load("id");
        }
        await context.sync();

        if (cc.isNullObject || cc.id !== objectItem.contentControlId) {
          removeListener();
          return;
        }

        const address: CellAddress = { row: cell.rowIndex, column: cell.cellIndex };
        const cellText = cell.body.text.trim();
        const currentList = cellListRef.current;
        const newCell: CoObjectCellItem = {
          ...objectItem,
          id: `${objectItem.id}_${address.row}-${address.column}`,
          cellAddress: address,
          info: {
            ...objectItem.info,
            objectType: CoObjectTypeEnum.TABLE_CELL,
            objectName: renderCellTitle(address, cellText),
          },
        };
        if (!currentList.some((c) => c.id === newCell.id)) {
          const newList = [...currentList, newCell];
          setCellList(newList);
          setExpanded(true);
          onCellListChange?.(newList);
          onAddCells?.(objectItem);

          // Auto-select newly added cell (minimal change)
          setSelectedCellKeys((prev) => new Set([...prev, cellKey(address)]));

          onSelectedCellsChange?.(objectItem, newList);
        }
        // keep listener active for more cells from the same table
      });
    } catch (err) {
      console.error("Errore onSelectionChanged:", err);
      removeListener();
    } finally {
      setShowPlaceholder(false);
    }
  };

  const handleAddCell = () => {
    if (handlerActiveRef.current) {
      removeListener();
      return;
    }
    // Deactivate any other row that's currently listening
    cancelActiveListener?.();
    cancelActiveListener = null;

    const handler = onSelectionChanged;
    handlerFnRef.current = handler;
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, handler, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        handlerActiveRef.current = true;
        cancelActiveListener = removeListener;
        setIsListening(true);
        onListeningChangeRef.current?.(true);
      } else {
        console.error("Errore attivazione handler:", result.error);
      }
    });
  };

  const handleCellToggleSelect = (item: CoObjectItem) => {
    const cell = item as CoObjectCellItem;
    if (!cell.cellAddress) return;

    const key = cellKey(cell.cellAddress);

    setSelectedCellKeys((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      const selectedItems = cellListRef.current.filter((c) => next.has(cellKey(c.cellAddress)));

      onSelectedCellsChange?.(objectItem, selectedItems);
      return next;
    });
  };
  const handleLinkClick = (event: { shiftKey: boolean }) => {
    onClick?.(objectItem, event.shiftKey);
  };

  const hasComponentIcon =
    showComponentIcons && (objectItem.info.isComponent || objectItem.info.componentReference !== undefined);

  const objectName =
    objectItem.info.objectType === CoObjectTypeEnum.DOCUMENT_PART_INCLUDE
      ? objectItem.info.objectName.toString().replace(/\.docx$/i, "")
      : objectItem.info.objectName;

  return (
    <div
      key={`div-${objectItem.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : undefined }}
    >
      <Stack horizontal key={`item-${objectItem.id}`} style={{ alignItems: "center", padding: "1px 0", width: "100%" }}>
        <button
          type="button"
          aria-label={expanded ? "Collapse" : "Expand"}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            marginRight: 4,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
        >
          <Icon iconName={expanded ? "ChevronDown" : "ChevronRight"} style={{ fontSize: 12 }} />
        </button>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center" }}>
          <Link
            style={{
              color: "var(--color-black)",
              cursor: onClick ? "pointer" : "default",
              flexGrow: 0,
              flexShrink: 1,
              minWidth: 0,
              overflow: "hidden",
            }}
            key={`link-${objectItem.id}`}
            id={generateIdentifier(objectItem.info.objectName, "objectlink")}
            onClick={handleLinkClick}
          >
            <div
              style={{
                backgroundColor: highlighted ? "rgb(237, 235, 233)" : null,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {showObjectIcon ? (
                <span className={`${objectItem.info.objectType} icon_class`} style={{ lineHeight: 1 }} />
              ) : null}
              <span style={{ lineHeight: 1 }}>{objectName}</span>
              {showStatusIcons && objectItem.isLocked ? (
                <img
                  src="/assets/lock.svg"
                  alt="Lock Icon"
                  style={{
                    objectFit: "contain",
                    paddingLeft: "5px",
                    backgroundRepeat: "no",
                    backgroundPosition: "right center",
                  }}
                />
              ) : null}
              {hasComponentIcon ? (
                <span
                  className={`${
                    objectItem.info.isComponent ? `component_title` : `instance_component_title`
                  } right_icon_class`}
                />
              ) : null}
            </div>
          </Link>
          <CustomPill
            color={isListening ? "var(--color-green)" : "var(--color-blue)"}
            style={{
              padding: "2px 8px",
              flexShrink: 0,
              marginLeft: 4,
              cursor: "pointer",
              opacity: isListening || isHovered ? 1 : 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddCell();
            }}
          >
            <span className={isListening ? "k-icon k-i-check-outline" : "k-icon k-i-plus-outline"} />{" "}
            {isListening
              ? formatMessageDefault("cof.base.done", "Done")
              : formatMessageDefault("cof.validation.button.cellselectionadd", "Add cell(s)")}
          </CustomPill>
        </div>
        {buttons.length > 0 && (
          <Stack horizontal style={{ marginLeft: "auto" }}>
            {buttons.map((button) => (
              <IconButton
                key={button.key}
                iconProps={{
                  imageProps: {
                    src: button.icon,
                    imageFit: ImageFit.contain,
                  },
                  styles: { root: { fontWeight: "bold" } },
                }}
                style={{ marginLeft: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  button.onClick(objectItem);
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {expanded && (cellList.length > 0 || showPlaceholder) && (
        <div style={{ paddingLeft: 20, display: "flex", flexDirection: "column", rowGap: 2 }}>
          {cellList.map((cell) => (
            <ObjectListItemRow
              key={cellKey(cell.cellAddress)}
              onClick={() => selectWordCell(cell.contentControlId, cell.cellAddress)}
              objectItem={cell}
              selected={selectedCellKeys.has(cellKey(cell.cellAddress))}
              selectable={true}
              onToggleSelect={handleCellToggleSelect}
              showStatusIcons={false}
            />
          ))}
          {showPlaceholder ? (
            <WithLoader isLoading data-testid="cell-placeholder">
              {null}
            </WithLoader>
          ) : null}
        </div>
      )}
    </div>
  );
};
